import { FC, useMemo } from 'react';
import { Box, Text, Badge, HStack } from '@chakra-ui/react';
import {
  SearchableSelect,
  SearchableSelectOption,
} from '@/components/common/SearchableSelect';
import { Dog, Gender } from '@/types/dog';
import { ParentMatchResult } from '@/utils/parentDogMatcher';

/**
 * ParentDogSelectorのプロパティ
 */
export interface ParentDogSelectorProps {
  /** 犬のリスト */
  dogs: Dog[];
  /** 選択対象の性別 */
  gender: Gender;
  /** 選択中の値（犬のID） */
  value?: string;
  /** 値が変更されたときのコールバック */
  onChange: (value: string) => void;
  /** ラベル */
  label: string;
  /** ヘルパーテキスト */
  helperText?: string;
  /** 除外するID（自分自身など） */
  excludeId?: string;
  /** 新規作成を許可するか */
  allowCreate?: boolean;
  /** 無効化 */
  disabled?: boolean;
  /** AIマッチング結果 */
  matchResult?: ParentMatchResult | null;
}

/**
 * マッチタイプに応じたバッジの色
 */
const getMatchBadgeColor = (
  matchType: ParentMatchResult['matchType']
): string => {
  switch (matchType) {
    case 'exact_registration':
      return 'green';
    case 'exact_name':
      return 'blue';
    case 'partial_name':
      return 'cyan';
    case 'fuzzy_name':
      return 'yellow';
    default:
      return 'gray';
  }
};

/**
 * 親犬選択コンポーネント
 * SearchableSelectをラップし、性別フィルタリングやAI解析結果連携を提供
 */
export const ParentDogSelector: FC<ParentDogSelectorProps> = ({
  dogs,
  gender,
  value,
  onChange,
  label,
  helperText,
  excludeId,
  allowCreate = false,
  disabled = false,
  matchResult,
}) => {
  // 性別でフィルタリングし、オプション形式に変換
  const options: SearchableSelectOption[] = useMemo(() => {
    return dogs
      .filter((dog) => dog.gender === gender)
      .filter((dog) => !excludeId || dog.id !== excludeId)
      .map((dog) => {
        // 検索用テキストを構築（登録番号全般を含む）
        const searchParts = [
          dog.name,
          dog.officialName,
          dog.bioNumber,
          dog.jkcRegistrationNumber,
          dog.dnaRegistrationNumber,
          dog.microchipNumber,
          dog.microchipPin,
        ].filter(Boolean);

        // 正式名称をメイン表示、空ならどうぶつ名にフォールバック
        const mainName = dog.officialName || dog.name;

        // 補助表示: どうぶつ名（正式名称がある場合）+ 登録番号
        const subParts: string[] = [];
        if (dog.officialName && dog.name) subParts.push(dog.name);
        if (dog.jkcRegistrationNumber) subParts.push(`JKC:${dog.jkcRegistrationNumber}`);
        if (dog.dnaRegistrationNumber) subParts.push(`DNA:${dog.dnaRegistrationNumber}`);
        if (dog.microchipNumber) subParts.push(`MC:${dog.microchipNumber}`);

        return {
          value: dog.id,
          label: mainName,
          searchText: searchParts.join(' '),
          subLabel: subParts.join(' / '),
        };
      });
  }, [dogs, gender, excludeId]);

  return (
    <Box>
      <HStack mb={2}>
        <Text fontSize="sm" fontWeight="medium">
          {label}
        </Text>
        {matchResult && matchResult.matchedDogId && value === matchResult.matchedDogId && (
          <Badge
            colorPalette={getMatchBadgeColor(matchResult.matchType)}
            size="sm"
          >
            AI一致: {matchResult.confidence}%
          </Badge>
        )}
        {value && value.startsWith('__new__:') && (
          <Badge colorPalette="orange" size="sm">
            新規登録
          </Badge>
        )}
      </HStack>

      <SearchableSelect
        options={options}
        value={value}
        onChange={onChange}
        placeholder="名前・生体番号・登録番号で検索"
        allowCreate={allowCreate}
        disabled={disabled}
      />

      {helperText && (
        <Text fontSize="xs" color="gray.600" mt={1}>
          {helperText}
        </Text>
      )}
    </Box>
  );
};

export default ParentDogSelector;
